import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import SEO, { SITE_URL } from '../components/SEO'
import ShopChat from '../components/ShopChat'
import LocationBar from '../components/ShopChat/locationbar'
import TrendingPanel from '../components/ShopChat/trendingpanel'
import ChatSidebar from '../components/ShopChat/chatsidebar'
import { Message } from '../components/ShopChat/shopchat'
import { activeChat, chatApi, useSignedIn } from '../utils/shopChats'
import { ShopPage, Hero, ShopTitle, ShopSubtitle, Section, SectionHeading, Grid, BusinessCard, CategoryCard } from '../components/ShopChat/shopcommon'
import { fetchWrapper } from '../utils/apiHandlers'
import { useShopLocation } from '../utils/shopLocation'
import theme from '../theme'

const SUGGESTIONS = ['Suggest something to lift my mood', 'Where should we go for a first date?', 'A gift for my mom', "I'm hungry"]

const ShopHome: React.FC = () => {
  const { location, ready } = useShopLocation()
  const [categories, setcategories] = useState<any[]>([])
  const [businesses, setbusinesses] = useState<any[]>([])
  const [nearby, setnearby] = useState(0)
  const [inject, setinject] = useState<{ text: string; n: number } | null>(null)

  // Saved conversations: which chat is open, its messages, and a key that remounts the chat when it changes.
  const signedIn = useSignedIn()
  const [cid, setcid] = useState('')
  const [initial, setinitial] = useState<Message[] | undefined>(undefined)
  const [chatkey, setchatkey] = useState(0)
  const [listkey, setlistkey] = useState(0)
  const [restored, setrestored] = useState(false)
  const [drawer, setdrawer] = useState(false)

  const open = async (target: string) => {
    const chat = await chatApi.load(target, location ? location.lat : undefined, location ? location.lon : undefined).catch(() => null)
    if (!chat) { startNew(); return }
    setinitial(chat.messages.map((m: any) => ({
      from: m.role === 'user' ? 'user' : 'bot', text: m.text, businesses: m.businesses, chatid: chat.cid, options: m.options,
    })))
    setcid(chat.cid)
    activeChat.set(chat.cid)
    setchatkey((k) => k + 1)
    setdrawer(false)
  }

  const startNew = () => {
    activeChat.set('')
    setcid('')
    setinitial(undefined)
    setchatkey((k) => k + 1)
    setdrawer(false)
  }

  // Refresh keeps the conversation (same tab); a new tab or window starts fresh.
  useEffect(() => {
    const saved = activeChat.get()
    if (saved) open(saved).finally(() => setrestored(true))
    else setrestored(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Signing in or out changes what the sidebar shows; a guest chat gets claimed by the account on its next message.
  useEffect(() => { setlistkey((k) => k + 1) }, [signedIn])

  useEffect(() => {
    fetchWrapper.get('/api/directory/categories').then((r: any) => setcategories(r.categories || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!ready) return
    const q = location ? `&lat=${location.lat}&lon=${location.lon}` : ''
    fetchWrapper.get(`/api/directory/list?limit=12${q}`)
      .then((r: any) => { setbusinesses(r.businesses || []); setnearby(r.nearby || 0) })
      .catch(() => {})
  }, [ready, location])

  const ask = (topic: string) => setinject({ text: topic, n: (inject ? inject.n : 0) + 1 })
  const place = location ? location.city : ''

  return (
    <ShopPage>
      <SEO
        title="Ask Amigo - Tell us what's on your mind, find local help | MiAmigo.AI"
        description="Ask Amigo is a friendly AI that knows your neighborhood. Tell it what you need or what's on your mind and it points you to real local places."
        path="/ask"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", "name": "Ask Amigo", "url": `${SITE_URL}/ask` }}
      />
      <Hero>
        <Kicker>Ask Amigo</Kicker>
        <ShopTitle>What's on your mind?</ShopTitle>
        <ShopSubtitle>Ask for anything, or just tell us what you're going through - a craving, a gift, a problem to solve. We'll point you to local businesses that can help.</ShopSubtitle>
        <LocationBar onDark />
      </Hero>

      <TopRow>
        <SideCol $open={drawer}>
          <ChatSidebar
            activeCid={cid} refreshKey={listkey} onSelect={open} onNew={startNew}
            onDeleted={(deleted) => { if (deleted === cid) startNew() }}
          />
        </SideCol>
        <ChatCol>
          <DrawerButton onClick={() => setdrawer(!drawer)}>{drawer ? 'Hide chats' : 'Chats'}</DrawerButton>
          {restored && (
            <ShopChat
              key={chatkey} suggestions={SUGGESTIONS} inject={inject} persist chatid={cid} initialMessages={initial}
              onSaved={(saved) => { setcid(saved); activeChat.set(saved); setlistkey((k) => k + 1) }}
            />
          )}
        </ChatCol>
        <TrendingCol>
          <TrendingPanel onPick={ask} fallback={SUGGESTIONS} />
        </TrendingCol>
      </TopRow>

      {categories.length > 0 && (
        <Section>
          <SectionHeading>Browse by category</SectionHeading>
          <Grid>{categories.map((c) => <CategoryCard key={c.key} c={c} />)}</Grid>
        </Section>
      )}

      {businesses.length > 0 && (
        <Section>
          <SectionHeading>{nearby > 0 && place ? `Near ${place}` : 'Local businesses'}</SectionHeading>
          {nearby === 0 && place && (
            <Hint>Nothing listed within 30 miles of {place} yet - showing what's available so far.</Hint>
          )}
          <Grid>{businesses.map((b) => <BusinessCard key={b.slug} b={b} />)}</Grid>
        </Section>
      )}
    </ShopPage>
  )
}

const TopRow = styled.div`
  width: 100%;
  max-width: 1280px;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 290px;
  gap: 20px;
  align-items: start;
  margin-top: -6px;
  @media (max-width: 1150px) {
    grid-template-columns: 240px minmax(0, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const SideCol = styled.div<{ $open: boolean }>`
  @media (max-width: 800px) {
    display: ${(p) => (p.$open ? 'block' : 'none')};
    order: 1;
  }
`

const ChatCol = styled.div`
  min-width: 0;
  @media (max-width: 800px) {
    order: 2;
  }
`

const TrendingCol = styled.div`
  @media (max-width: 1150px) {
    grid-column: 1 / -1;
    max-width: 640px;
  }
  @media (max-width: 800px) {
    order: 3;
  }
`

const DrawerButton = styled.button`
  display: none;
  margin-bottom: 8px;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.md};
  padding: 7px 14px;
  font-weight: 600;
  cursor: pointer;
  @media (max-width: 800px) {
    display: inline-block;
  }
`

const Kicker = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #cfe4ff;
`

const Hint = styled.div`
  font-size: 13px;
  color: ${theme.colors.textMuted};
  margin: -6px 0 12px;
`

export default ShopHome
